import type { KeyboardEvent } from 'react';
import { Image } from '../design-system';
import { PotentialHitPricePill } from '../design-system/PotentialHitPricePill';
import type { IframePoolItem } from '../api';
import { formatPrice } from '../currency';

export interface PotentialHitItemProps {
  item: IframePoolItem;
  onClick?: (item: IframePoolItem) => void;
}

const CARD_BG =
  'linear-gradient(180deg, #C2C3C5 -16.4%, #F7F8F8 121.88%), #FFF';

export function PotentialHitItem({ item, onClick }: PotentialHitItemProps) {
  const interactive = typeof onClick === 'function';
  const priceLabel = formatPrice(item.itemPrice, 2);
  return (
    <article
      data-co-component="PotentialHitItem"
      className={
        'relative aspect-[195/287] overflow-hidden ' +
        (interactive
          ? 'cursor-pointer transition-transform duration-150 hover:scale-[1.02] active:scale-[0.98]'
          : '')
      }
      style={{
        overflow: 'hidden',
        background: `var(--co-PotentialHitItem-background, ${CARD_BG})`,
        borderRadius: 'var(--co-PotentialHitItem-borderRadius, 20px)',
        borderStyle: 'solid',
        borderWidth: 'var(--co-PotentialHitItem-borderWidth, 1px)',
        borderColor: 'var(--co-PotentialHitItem-borderColor, #EAEAEB)',
      }}
      aria-label={item.title || 'Potential hit'}
      {...(interactive
        ? {
            role: 'button',
            tabIndex: 0,
            onClick: () => onClick!(item),
            onKeyDown: (e: KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick!(item);
              }
            },
          }
        : {})}
    >
      {/* Inset image to the padding box and round it at the *inner* radius
          (outer radius − border width). The DS Image wrapper uses
          `rounded-[inherit]`, so without this it would inherit the full
          outer radius and over-round its corners — exposing the card
          background between the border and the photo on thick borders. */}
      <div
        className="absolute inset-0"
        style={{
          borderRadius:
            'calc(var(--co-PotentialHitItem-borderRadius, 20px) - var(--co-PotentialHitItem-borderWidth, 1px))',
        }}
      >
        <Image
          src={item.imageUrl}
          alt={item.title || 'Potential hit'}
          className="absolute inset-0"
          imgClassName="absolute inset-0 h-full w-full object-cover"
          width={195}
          height={287}
          quality={95}
        />
      </div>

      {priceLabel && <PotentialHitPricePill price={priceLabel} />}
    </article>
  );
}
